import React, { useMemo } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { ng } from "@common/ngds";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import type { NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { AlertMessage } from "../ui-components/SXAlert/SXAlertMessage";
import { useProject } from "../../features/projects/projectSlice";
import {
  DEFAULT_FIRE_DURATION_MINUTES,
  hasFireCombination,
  timberProtectionHasLayerThicknesses,
  timberProtectionHasThickness,
} from "./shared";

const TIMBER_FIRE_MATERIALS: Array<{ key: string; id: ng.fire_protection.timber.MaterialType }> = [
  { key: "wood_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_WOOD },
  {
    key: "gypsum_board_ah1_internal_",
    id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH1_INTERNAL,
  },
  { key: "gypsum_board_ah1_other_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH1_OTHER },
  {
    key: "gypsum_board_ah2_internal_",
    id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH2_INTERNAL,
  },
  { key: "gypsum_board_ah2_other_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_AH2_OTHER },
  {
    key: "gypsum_board_f1_internal_",
    id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F1_INTERNAL,
  },
  { key: "gypsum_board_f1_other_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F1_OTHER },
  {
    key: "gypsum_board_f2_internal_",
    id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F2_INTERNAL,
  },
  { key: "gypsum_board_f2_other_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F2_OTHER },
  { key: "rock_fibre_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_ROCK_FIBER },
  { key: "user_defined_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_USER_DEFINED },
  { key: "none_", id: ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE },
];

type TimberFireProtSide = "top" | "bottom" | "left" | "right";

function showTimberProtComp(type: string, mtrlType: ng.fire_protection.timber.MaterialType): boolean {
  if (mtrlType === ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE) return false;
  switch (type) {
    case "t":
      return timberProtectionHasThickness(mtrlType);
    case "ti":
    case "to":
      return timberProtectionHasLayerThicknesses(mtrlType);
    case "protCharRate":
      return mtrlType === ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_WOOD;
    case "failTime":
      return mtrlType >= ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_GYPSUM_BOARD_F1_INTERNAL;
    case "charStartTime":
      return mtrlType >= ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_ROCK_FIBER;
    case "charRateModFact":
      return mtrlType === ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_USER_DEFINED;
    default:
      return false;
  }
}

export function SXInputTimberFireSettings() {
  const { t } = useTranslation();
  const {
    project: selProject,
    setTimberFireDuration,
    setTimberCharringRateAuto,
    setTimberCharringRate,
    setTimberStructProtection,
    setTimberCharringStartTime,
    setTimberFireMaterialType,
    setTimberFireMaterialProps,
  } = useProject();

  const fireSettingsDisabled = !hasFireCombination(selProject);

  const element = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0] as
    | { designSettings?: ng.design.ElementDesignSettings_json }
    | undefined;
  const timberFire = element?.designSettings?.timber?.fire;
  const generalFire = element?.designSettings?.general?.generalFire;

  const duration =
    typeof generalFire?.durationOfFire === "number" ? generalFire.durationOfFire : DEFAULT_FIRE_DURATION_MINUTES;
  const charRateAuto = timberFire?.charringRate?.calculateAuto ?? true;
  const charRate = typeof timberFire?.charringRate?.betaN === "number" ? timberFire.charringRate.betaN : 0.7;

  const protection = timberFire?.protection;
  const mtrlProps = protection?.mtrl?.properties;

  const protTop = protection?.structProtectionTop ?? false;
  const protBottom = protection?.structProtectionBottom ?? false;
  const protLeft = protection?.structProtectionLeft ?? false;
  const protRight = protection?.structProtectionRight ?? false;

  const charTimeTop = Number(protection?.charringStartTimeTop ?? 0);
  const charTimeBottom = Number(protection?.charringStartTimeBottom ?? 0);
  const charTimeLeft = Number(protection?.charringStartTimeLeft ?? 0);
  const charTimeRight = Number(protection?.charringStartTimeRight ?? 0);

  const rawMtrlType = mtrlProps?.materialType;
  const mtrlType =
    rawMtrlType != null
      ? ng.fire_protection.timber.MaterialType_FromObject(rawMtrlType)
      : ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE;

  const thickness = Number(mtrlProps?.thickness ?? 13e-3);
  const thicknessInner = Number(mtrlProps?.thicknessInner ?? 13e-3);
  const thicknessOuter = Number(mtrlProps?.thicknessOuter ?? 13e-3);
  const protCharRate = Number(mtrlProps?.charringRate ?? 0.8);
  const failTime = Number(mtrlProps?.failureTime ?? 10);
  const charStartTime = Number(mtrlProps?.charringStartTime ?? 10);
  const charRateModFact = Number(mtrlProps?.charringRateModFactor ?? 0.7);

  const materialItems = useMemo<NGDropdownListItem<ng.fire_protection.timber.MaterialType>[]>(
    () => TIMBER_FIRE_MATERIALS.map(({ key, id }) => ({ id, name: t(`${key}.label`) })),
    [t],
  );

  const selectedMaterial = materialItems.find((item) => item.id === mtrlType) ?? null;

  const showInvalidDurationAlert = () => {
    AlertMessage("warning", t("warning_.label"), "", t("fire_warning_duration_not_valid_.label"));
  };

  const protectionSides: Array<{ side: TimberFireProtSide; label: string; enabled: boolean; charTime: number }> = [
    { side: "top", label: t("top_.label"), enabled: protTop, charTime: charTimeTop },
    { side: "bottom", label: t("bottom_.label"), enabled: protBottom, charTime: charTimeBottom },
    { side: "left", label: t("left_.label"), enabled: protLeft, charTime: charTimeLeft },
    { side: "right", label: t("right_.label"), enabled: protRight, charTime: charTimeRight },
  ];

  return (
    <Container fluid id="sx-table-container" className="ps-0 px-0">
      <Container style={{ color: "#696969" }}>
        {fireSettingsDisabled && (
          <Row className="p-2 border rounded mb-2" style={{ borderColor: "var(--panel-border)" }}>
            <Col>
              <div className="mt-2" style={{ color: "#696969" }}>
                <span style={{ fontWeight: "bold" }}>{t("fire_load_combination_needs_to_be_added_.label")}</span>
              </div>
            </Col>
          </Row>
        )}

        <Row className="container-row" style={{ alignItems: "end" }}>
          <Col xs={5} className="ps-0">
            <div className="editfield-label">
              <span>{t("duration_of_fire_.label")}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="ps-0 px-0" style={{ height: 32, flex: 1 }}>
                <SXEditField
                  id="sx-timber-fire-duration"
                  type="number"
                  val={duration}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(value) => {
                    const nextValue = Number(value);
                    if (Number.isFinite(nextValue) && nextValue > 0 && nextValue <= 1000) {
                      setTimberFireDuration(nextValue);
                      return;
                    }
                    showInvalidDurationAlert();
                  }}
                />
              </div>
              <var>{t("minutes_.label")}</var>
            </div>
          </Col>

          <Col xs={5}>
            <div className="editfield-label">
              <span>
                <Trans
                  i18nKey="charring_rate_betan.label"
                  defaults="Charring rate, beta<sub>n</sub>"
                  components={{ sub: <sub /> }}
                />
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, height: 32 }}>
              <Form.Check
                type="switch"
                className="sx-switch"
                id="sx-timber-charrate-auto"
                label={t("automatic_.label")}
                checked={charRateAuto}
                disabled={fireSettingsDisabled}
                onChange={(e) => setTimberCharringRateAuto(e.target.checked)}
              />
              {!charRateAuto && (
                <SXEditField
                  id="sx-timber-charrate"
                  type="number"
                  val={charRate}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberCharringRate(Number(v))}
                />
              )}
            </div>
          </Col>
        </Row>

        {/** Fire Protection */}
        {/* {mtrlType !== ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE && ( */}
        <Container id="fire-protection-timber" style={{ color: "#696969" }} className="mt-2 ps-0 pe-0">
          <Row className="container-row" style={{ alignItems: "center", fontWeight: "bold" }}>
            <Col xs={12} className="editfield-label ps-0 pb-0">
              {t("fire_protection_.label")}
            </Col>
          </Row>
          <Row className="editfield-label mb-1">{t("protecting_structure_at_.label")}...</Row>
          <Row className="container-row mb-2 pt-0">
            {protectionSides.map(({ side, label, enabled }) => (
              <Col xs={3} key={side}>
                <Form.Check
                  type="switch"
                  className="sx-switch"
                  label={label}
                  id={`sx-timber-prot-${side}`}
                  checked={enabled}
                  disabled={fireSettingsDisabled}
                  onChange={(e) => setTimberStructProtection(side, e.target.checked)}
                />
              </Col>
            ))}
          </Row>

          <Row className="editfield-label">
            {t("charring_start_time_.label")} ({t("minutes_.label")})
          </Row>
          <Row className="container-row ps-0" style={{ alignItems: "center" }}>
            {protectionSides.map(({ side, enabled, charTime }) => (
              <Col xs={3} key={side} className="ps-0" style={{ height: 32 }}>
                <SXEditField
                  id={`sx-timber-chartime-${side}`}
                  type="number"
                  val={charTime}
                  disabled={fireSettingsDisabled || !enabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberCharringStartTime(side, Number(v))}
                />
              </Col>
            ))}
          </Row>
        </Container>
        {/* )} */}
        {/** END /Fire Protection */}

        <Row className="container-row" style={{ alignItems: "center" }}>
          <Col xs={12} className="editfield-label ps-0">
            {t("protecting_material_.label")}
          </Col>
          <Col xs={12} className="ps-0">
            <SXDropdown
              id="sx-timber-mtrl-dropdown"
              toggleId="sx_timber_mtrl_toggle"
              className="sx-dropdown"
              dataTestId="dropdown_timber_mtrl"
              name="timber_mtrl"
              lang="en"
              disabled={fireSettingsDisabled}
              list={materialItems}
              val={selectedMaterial}
              onUpdate={(value) => value && setTimberFireMaterialType(value.id)}
              rawNames
              localisation={false}
            />
          </Col>
          {/* <Col>
            <OverlayTrigger
              trigger="click"
              rootClose
              placement="right"
              overlay={
                <Popover id="sx-timber-fire-material-info-popover">
                  <Popover.Header as="h3">{t("material_.label")}</Popover.Header>
                  <Popover.Body>
                    {showTimberProtComp("t", mtrlType) && (
                      <div className="mb-1">
                        <strong>{t("fire_protection_thickness_.label")}: </strong>
                        <span>{thickness * 1000} mm</span>
                      </div>
                    )}
                    {showTimberProtComp("ti", mtrlType) && (
                      <div className="mb-1">
                        <strong>{t("fire_protection_thickness_inner_.label")}: </strong>
                        <span>{thicknessInner * 1000} mm</span>
                      </div>
                    )}
                    {showTimberProtComp("to", mtrlType) && (
                      <div className="mb-1">
                        <strong>{t("fire_protection_thickness_outer_.label")}: </strong>
                        <span>{thicknessOuter * 1000} mm</span>
                      </div>
                    )}
                    {showTimberProtComp("protCharRate", mtrlType) && (
                      <div className="mb-1">
                        <strong>β₀: </strong>
                        <span>{protCharRate} mm/min</span>
                      </div>
                    )}
                    {showTimberProtComp("failTime", mtrlType) && (
                      <div className="mb-1">
                        <strong>{t("failure_time_.label")}: </strong>
                        <span>
                          {failTime} {t("minutes_.label")}
                        </span>
                      </div>
                    )}
                    {showTimberProtComp("charStartTime", mtrlType) && (
                      <div className="mb-1">
                        <strong>{t("charring_start_time_.label")}: </strong>
                        <span>
                          {charStartTime} {t("minutes_.label")}
                        </span>
                      </div>
                    )}
                    {showTimberProtComp("charRateModFact", mtrlType) && (
                      <div>
                        <strong>{t("charring_rate_modification_factor_.label")}: </strong>
                        <span>{charRateModFact}</span>
                      </div>
                    )}
                  </Popover.Body>
                </Popover>
              }
            >
              <Button
                variant="link"
                id="sx-timber-fire-material-info-btn"
                aria-label={t("material_.label")}
                disabled={
                  fireSettingsDisabled || mtrlType === ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE
                }
                style={{
                  marginLeft: 8,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: "1px solid var(--btn-border-color)",
                  color: "var(--text-gray)",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                i
              </Button>
            </OverlayTrigger>
          </Col> */}
        </Row>
      </Container>

      {mtrlType !== ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE && (
        <Container>
          {showTimberProtComp("t", mtrlType) && (
            <Row className="container-row">
              <Col className="editfield-label">{t("fire_protection_thickness_.label")} (mm)</Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXEditField
                  id="sx-timber-thickness"
                  type="number"
                  fullWidth
                  textAlign="left"
                  val={thickness * 1000}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberFireMaterialProps({ thickness: Number(v) / 1000 })}
                />
              </Col>
            </Row>
          )}

          {showTimberProtComp("ti", mtrlType) && (
            <Row className="container-row">
              <Col className="editfield-label">{t("fire_protection_thickness_inner_.label")} (mm)</Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXEditField
                  id="sx-timber-thickness-inner"
                  type="number"
                  fullWidth
                  textAlign="left"
                  val={thicknessInner * 1000}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberFireMaterialProps({ thicknessInner: Number(v) / 1000 })}
                />
              </Col>
            </Row>
          )}

          {showTimberProtComp("to", mtrlType) && (
            <Row className="container-row">
              <Col className="editfield-label">{t("fire_protection_thickness_outer_.label")} (mm)</Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXEditField
                  id="sx-timber-thickness-outer"
                  type="number"
                  fullWidth
                  textAlign="left"
                  val={thicknessOuter * 1000}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberFireMaterialProps({ thicknessOuter: Number(v) / 1000 })}
                />
              </Col>
            </Row>
          )}

          {showTimberProtComp("protCharRate", mtrlType) && (
            <Row className="container-row">
              <Col className="editfield-label">
                <Trans
                  i18nKey="charring_rate_beta0.label"
                  defaults="Charring rate, beta<sub>0</sub>"
                  components={{ sub: <sub /> }}
                />{" "}
                (mm/min)
              </Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXEditField
                  id="sx-timber-prot-charrate"
                  type="number"
                  fullWidth
                  textAlign="left"
                  val={protCharRate}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberFireMaterialProps({ charringRate: Number(v) })}
                />
              </Col>
            </Row>
          )}

          {showTimberProtComp("failTime", mtrlType) && (
            <Row className="container-row">
              <Col className="editfield-label">
                {t("failure_time_.label")} {t("minutes_.label")}
              </Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXEditField
                  id="sx-timber-failtime"
                  fullWidth
                  textAlign="left"
                  type="number"
                  val={failTime}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberFireMaterialProps({ failureTime: Number(v) })}
                />
              </Col>
            </Row>
          )}

          {showTimberProtComp("charStartTime", mtrlType) && (
            <Row className="container-row">
              <Col className="editfield-label">
                {t("charring_start_time_.label")} {t("minutes_.label")}
              </Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXEditField
                  id="sx-timber-char-starttime"
                  fullWidth
                  textAlign="left"
                  type="number"
                  val={charStartTime}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberFireMaterialProps({ charringStartTime: Number(v) })}
                />
              </Col>
            </Row>
          )}

          {showTimberProtComp("charRateModFact", mtrlType) && (
            <Row className="container-row" style={{ alignItems: "center" }}>
              <Col xs={5} className="editfield-label ps-0">
                {t("charring_rate_modification_factor_.label")}
              </Col>
              <Col xs={3}>
                <SXEditField
                  id="sx-timber-charratemod"
                  type="number"
                  val={charRateModFact}
                  disabled={fireSettingsDisabled}
                  disableNegativeNumber
                  onUpdate={(v) => setTimberFireMaterialProps({ charringRateModFactor: Number(v) })}
                />
              </Col>
            </Row>
          )}
        </Container>
      )}
    </Container>
  );
}
